import { z } from 'zod';
import { uuidSchema, timestampSchema } from './common';
import { billingPlanTypeSchema, planKeySchema, type BillingPlanType, type PlanKey } from './billing';
import { isIsoTimestamp } from '../time';
import { MAX_EVAL_RETENTION_DAYS } from './setup/hubs';
import { mobileAppVersionSchema } from './mobile-version-policy';
import {
  DEBUG_READ_DEFAULT_LIMIT,
  DEBUG_READ_MAX_LIMIT,
  dataExplorerDebugDoType,
  type DataExplorerDebugDoType,
  type DataExplorerDebugHubCounts,
  type DataExplorerDebugOrgCounts,
  type DataExplorerDebugPublishState,
} from './debug-read';
import { TURN_DEADLINE_SECONDS_FLOOR, TURN_DEADLINE_SECONDS_MAX } from './turn-deadline';

// =============================================================================
// REQUEST SCHEMAS
// =============================================================================

// --- Param schemas ---

export const adminOrgIdParam = z.object({
  orgId: uuidSchema,
});

export const adminOrgAdminIdParam = z.object({
  orgId: uuidSchema,
  // `adminId` is a `member_grant.user_id`, which for a logged-in user is the
  // WorkOS `sub` (`user_01…`), not a UUID. Matches `orgAdminIdParam` on the
  // org-facing route.
  adminId: z.string().min(1),
});

export const adminUserIdParam = z.object({
  userId: uuidSchema,
});

/**
 * A seeded `pricing_plan_id` — `pp-` plus a lowercase slug (`pp-free`, `pp-solo`,
 * `pp-team`, `pp-growth-pack`). That is the id format every live row carries; see
 * `apps/backend/src/durable-objects/platform-do/seed.ts`.
 */
const PRICING_PLAN_ID_RE = /^pp-[a-z0-9]+(-[a-z0-9]+)*$/;

/**
 * A UUID is still accepted beside the slug: rows minted before the `pp-` naming (the
 * seed deactivates them but keeps them for audit) and any row `upsertPricingPlan`
 * created through its `crypto.randomUUID()` fallback are addressed by UUID. Deliberately
 * NOT a bare string — the id selects the row this route writes, so an unconstrained
 * param is how a typo reaches storage.
 */
export const adminPlanIdParam = z.object({
  planId: z.string().refine(
    (value) => PRICING_PLAN_ID_RE.test(value) || uuidSchema.safeParse(value).success,
    { message: 'planId must be a pricing plan id such as "pp-free", or a UUID' },
  ),
});

// --- Query schemas ---

export const adminOrganizationsQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  search: z.string().optional().default(''),
});

export const adminUserSearchQuery = z.object({
  email: z.string().min(3, 'email query parameter must be at least 3 characters'),
});

// One-time kanban slug migration query. Default is dry-run for safety.
export const kanbanSlugMigrationQuery = z.object({
  dry_run: z.enum(['true', 'false']).optional(),
});

// --- Body schemas ---

export const adjustQuotaBody = z.object({
  metric_type: z.string().min(1, 'metric_type is required'),
  action: z.enum(['add', 'set']),
  value: z.number().int().min(0, 'value must be a non-negative number'),
});

/**
 * PATCH /api/admin/organizations/:orgId/plan — both fields optional, at least one
 * required. `plan_type` stayed mandatory only because it was the sole field; a
 * window-only edit must not be forced to restate a plan it is not changing, and the
 * route relies on absence (never `undefined`) to leave `plan_type` out of the OrgDO
 * write entirely.
 */
export const updatePlanBody = z.object({
  /**
   * `comped` is the operator's lever, not `paid`. `paid` means "Stripe is billing this
   * org", and the route refuses it on a row with no billing period for that reason — the
   * shape it used to accept is the one that left an org unprojectable to Rekor and 503'd
   * every Data/Bases provisioning operation. It stays in the enum only to repair a row
   * whose Stripe period exists but whose `plan_type` drifted.
   */
  plan_type: billingPlanTypeSchema.optional(),
  /**
   * What the org is entitled TO, once `plan_type` says how it is paid for. Set it beside
   * `plan_type: 'comped'` to grant a paid plan's allowances without a subscription; the
   * Rekor projection reads the pricing row for this key. Stripe owns this column on a
   * `paid` row, so the route refuses to move it there.
   */
  plan_key: planKeySchema.optional(),
  /**
   * Move the start of the org's rolling window. The operator lever for a window that
   * must be backdated — `free_plan_started_at` is otherwise only ever stamped to *now*
   * (org creation, `cancelBilling`, entering `comped`, the lazy rollover), so an elapsed
   * window is unreachable without it.
   *
   * Canonical form only (AGENTS.md › Timestamps): `timestampSchema` already refuses a
   * non-UTC offset and `isIsoTimestamp` pins the millisecond precision, because this
   * value is stored as-is and read back by `rollingWindowStart()` / `rollingWindowEnd()`
   * and by the Rekor projection's `window_start`.
   *
   * Past-or-now only. A future start freezes the org's window permanently:
   * `maybeRolloverBillingWindow` derives a NEGATIVE elapsed and so never archives the
   * metrics nor restamps the column, while the rollover sweep selects only windows
   * that have already passed — so nothing rolls the org over, its consumed operations
   * never reset, and past quota every AI turn is refused until a human re-patches.
   */
  free_plan_started_at: timestampSchema
    .refine(isIsoTimestamp, {
      message: 'free_plan_started_at must be ISO-8601 UTC with millisecond precision (e.g. 2026-07-01T00:00:00.000Z)',
    })
    .refine((value) => Date.parse(value) <= Date.now(), {
      message: 'free_plan_started_at must not be in the future',
    })
    .optional(),
}).strip().refine(
  (data) => Object.keys(data).length > 0,
  { message: 'No fields to update' }
);

export const addAdminUserBody = z.object({
  user_email: z.string().email('user_email must be a valid email address'),
});

/**
 * The highest voice-call price `PATCH /admin/config` accepts, in operations per connected
 * call-minute: ten times the residency meter's own rate (1 per 10 s, 6 a minute).
 */
export const MAX_VOICE_CALL_MINUTE_OPS = 60;

/**
 * The voice-call meter's price: the operations a call bills per connected minute of its
 * voice agent, on top of each call turn's initiation (owner decision 19, D5). 0, the
 * default, bills a call's minutes nothing. A whole number, capped at
 * `MAX_VOICE_CALL_MINUTE_OPS` so a mistyped price cannot bill a call hundreds of times over.
 */
export const voiceCallMinuteOps = z.number().int().min(0).max(MAX_VOICE_CALL_MINUTE_OPS);

/**
 * How many automation gate evaluations bill one operation
 * (`platform_config.automation_gate_evaluations_per_operation`, wayai#4931 owner decision 7), for
 * both gates — a fire gate's, and an item gate's for each contact: a gate that decided bills a 1/N
 * share of an operation, so an automation that a gate keeps skipping still pays for its
 * evaluations, and what a gate lets through also bills its own work. The bounds of the value an
 * admin may set; its seed is the PlatformDO column's default.
 */
export const AUTOMATION_GATE_EVALUATIONS_PER_OPERATION_BOUND = 1000;
export const automationGateEvaluationsPerOperation = z.number().int().min(1).max(AUTOMATION_GATE_EVALUATIONS_PER_OPERATION_BOUND);

/**
 * How many days a hub keeps an automation's runs with their targets
 * (`platform_config.automation_run_retention_days`, wayai#4931 PR 8a): an older run is deleted by
 * the hub's next automation activity, except a run still reading its list (`running`, started
 * within the batch's overlap window) and a gate evaluation not yet billed, which keeps its row
 * and its run's until it is. The bounds of the value an admin may set; its seed is the PlatformDO
 * column's default.
 */
export const AUTOMATION_RUN_RETENTION_DAYS_MAX = 3650;
export const automationRunRetentionDays = z.number().int().min(1).max(AUTOMATION_RUN_RETENTION_DAYS_MAX);

// The deadline's bounds, `TURN_DEADLINE_SECONDS_FLOOR` and `TURN_DEADLINE_SECONDS_MAX`, are
// declared in `./turn-deadline` (it says why).
export const turnDeadlineSeconds = z.number().int().min(TURN_DEADLINE_SECONDS_FLOOR).max(TURN_DEADLINE_SECONDS_MAX);

export const updatePlatformConfigBody = z.object({
  free_plan_max_free_orgs_per_user: z.number().int().min(0).optional(),
  /** Kill switch for admin debug-read endpoints. 0 = off, 1 = on. */
  debug_read_enabled: z.number().int().min(0).max(1).optional(),
  /**
   * The harness rollout gate + kill switch — the ONLY harness gate. 0 = off
   * (default-deny: agent create/update is refused AND the per-turn kill switch
   * aborts every harness turn on its next invocation), 1 = on. `wayai admin harness
   * disable|enable` flips this. The DO's `updatePlatformConfig` allowlist already
   * accepts it; this exposes it on the route.
   */
  harness_enabled: z.number().int().min(0).max(1).optional(),
  /**
   * Default eval-session retention window in days, used by every hub that sets no
   * `eval_retention_days` override. `0` disables eval retention platform-wide.
   * NOT NULL in the DDL, so the field is non-nullable here — clearing is not a
   * state; setting 0 is.
   */
  default_eval_retention_days: z.number().int().min(0).max(MAX_EVAL_RETENTION_DAYS).optional(),
  /**
   * The voice-calls kill switch. 0 = off (the default: every voice-call surface stays dark
   * for every org), 1 = on (every org; a hub takes calls once it is configured for them).
   * `wayai admin voice-calls enable|disable` flips this.
   */
  voice_calls_enabled: z.number().int().min(0).max(1).optional(),
  /** The voice-call meter's price (`voiceCallMinuteOps`). `wayai admin voice-calls price` sets it. */
  voice_call_minute_ops: voiceCallMinuteOps.optional(),
  /**
   * The person-to-person threads kill switch (contacts, direct messages and groups; gh #4661).
   * 0 = off (the default: every `/api/contacts` route answers 404), 1 = on.
   * `wayai admin threads enable|disable` flips this.
   */
  threads_enabled: z.number().int().min(0).max(1).optional(),
  /**
   * The communities kill switch (gh #4872, Decision 14). 0 = off (the default), 1 = on. The
   * community member surface is open only while this AND `threads_enabled` are on.
   * `wayai admin communities enable|disable` flips this.
   */
  communities_enabled: z.number().int().min(0).max(1).optional(),
  /** How many automation gate evaluations bill one operation (`automationGateEvaluationsPerOperation`). */
  automation_gate_evaluations_per_operation: automationGateEvaluationsPerOperation.optional(),
  /** How many days a hub keeps an automation's runs (`automationRunRetentionDays`). */
  automation_run_retention_days: automationRunRetentionDays.optional(),
  /** How long a native agent turn may run (`turnDeadlineSeconds`). */
  turn_deadline_seconds: turnDeadlineSeconds.optional(),
  /**
   * The mobile app version policy (`MOBILE_VERSION_POLICY_COLUMNS`), per platform: an install
   * below the minimum is blocked until it updates from the store; one below the recommended
   * version is asked, dismissibly, to update. MAJOR.MINOR.PATCH. Raise a minimum only once
   * the store serves a version that meets it — the blocked screen links to the store.
   */
  mobile_ios_minimum_version: mobileAppVersionSchema.optional(),
  mobile_ios_recommended_version: mobileAppVersionSchema.optional(),
  mobile_android_minimum_version: mobileAppVersionSchema.optional(),
  mobile_android_recommended_version: mobileAppVersionSchema.optional(),
}).strip().refine(
  (data) => Object.keys(data).length > 0,
  { message: 'No fields to update' }
);

export const updateFreeOrgLimitBody = z.object({
  max_free_orgs_override: z.number().int().min(0).nullable(),
});

/**
 * The scope/id shape shared by the platform-admin fan-out endpoints
 * (`POST /admin/harness/mass-destroy`, `POST /admin/hubs/repair-team-orphans`).
 *
 * Exactly ONE id must match `scope` (enforced by the superRefine): `org` needs
 * `org_id` (and no `hub_id`); `hub` needs `hub_id` (and no `org_id`); `all` takes
 * neither. `dry_run` reports the blast radius without acting — run it first.
 * `.strict()` rejects unknown keys so a typo'd scope-key can't widen the target.
 */
const adminScopedTargetShape = z.object({
  scope: z.enum(['all', 'org', 'hub']),
  org_id: z.string().uuid().optional(),
  hub_id: z.string().optional(),
  dry_run: z.boolean().optional().default(false),
});

export const adminScopedTargetBody = adminScopedTargetShape.strict().superRefine(refineScopedTarget);

/** The scope ↔ id pairing rule, shared by every body built on the scoped-target shape. */
function refineScopedTarget(
  data: { scope: 'all' | 'org' | 'hub'; org_id?: string; hub_id?: string },
  ctx: z.RefinementCtx,
): void {
  if (data.scope === 'org') {
    if (!data.org_id) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'scope=org requires org_id', path: ['org_id'] });
    }
    if (data.hub_id) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'scope=org must not include hub_id', path: ['hub_id'] });
    }
  } else if (data.scope === 'hub') {
    if (!data.hub_id) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'scope=hub requires hub_id', path: ['hub_id'] });
    }
    if (data.org_id) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'scope=hub must not include org_id', path: ['org_id'] });
    }
  } else {
    // scope === 'all' — platform-wide, so neither id is allowed.
    if (data.org_id) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'scope=all must not include org_id', path: ['org_id'] });
    }
    if (data.hub_id) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'scope=all must not include hub_id', path: ['hub_id'] });
    }
  }
}

/**
 * POST /api/admin/harness/mass-destroy — the operator incident-response reap
 * (harness-agents PR 2.9b). Enumerates live harness slots in `scope` and reaps
 * each conversation's harness session + MCP token + concurrency slot on demand.
 * Complements the automated per-turn kill switch (PR 2.9a), which only fires when
 * a conversation next turns; this reclaims what is live RIGHT NOW.
 */
export const adminHarnessMassDestroyBody = adminScopedTargetBody;

/**
 * POST /api/admin/hubs/repair-team-orphans — purge `hub_team_id` references left
 * dangling by a team deletion that predates the `deleteHubTeam` cascade, and clear
 * the removed memberships' UserDO mirror. Idempotent; `dry_run` first.
 */
export const adminRepairTeamOrphansBody = adminScopedTargetBody;

/**
 * POST /api/admin/hubs/repair-knowledge-blobs — the one-time sweep of the R2 objects hub knowledge
 * left behind (wayai-pro/wayai#5142 step 5 PR C1; decision 185's Q5). HubDO v105 deleted knowledge's
 * rows, and a migration cannot reach R2. Per hub it lists `{hub_id}/` in the resource bucket, then
 * asks HubDO which of those keys a `file` row still holds, and deletes only keys that none holds and
 * that were uploaded before `KNOWLEDGE_BLOB_MIN_AGE_HOURS`, never under `eval-attachments/` or
 * `call-openings/`. A DRY RUN BY DEFAULT: `dry_run: false` deletes.
 */
export const adminRepairKnowledgeBlobsBody = adminScopedTargetShape
  .extend({ dry_run: z.boolean().optional().default(true) })
  .strict()
  .superRefine(refineScopedTarget);

/**
 * How old an unreferenced key must be before the knowledge-blob repair deletes it: an upload writes
 * its object before its `file` row, so a younger key may be a live upload still in flight.
 */
export const KNOWLEDGE_BLOB_MIN_AGE_HOURS = 24;

/** Most keys one hub's result lists by name; the counts cover the rest. */
export const KNOWLEDGE_BLOB_MAX_KEYS_SHOWN = 20;

/**
 * Entity types `POST /admin/hubs/repair-branching-orphans` will delete when an operator
 * names one: the types sync's own stale-row delete handles, minus three with no repair
 * target. `hub_team` is customer-managed on a production hub (team CRUD is allowed there),
 * and `state` / `automation` carry a unique slug / name, so a sync can never have created a
 * second copy of one.
 */
export const BRANCHING_ORPHAN_ENTITY_TYPES = [
  'agent',
  'agent_parameter',
  'agent_resource',
  'channel',
  'connection',
  'eval',
  'mcp_tool',
  'resource',
  'resource_folder',
  'tool',
] as const;
export type BranchingOrphanEntityType = (typeof BRANCHING_ORPHAN_ENTITY_TYPES)[number];

/** Most rows one delete-mode call may name. The operator names each one by hand. */
export const BRANCHING_ORPHAN_MAX_TARGETS = 50;

/**
 * POST /api/admin/hubs/repair-branching-orphans — the #4577 remediation.
 *
 * Without `targets` it only REPORTS, for every production hub in `scope`: each row that no
 * live preview's entity mapping reaches and that shares its identity with a row one does
 * (a "twin" — the same preview entity created twice by overlapping syncs). It never
 * deletes in that mode.
 *
 * With `targets` (scope=hub only) it deletes exactly the named rows, each only after it
 * is verified unmapped by EVERY live preview linked to the hub and unreferenced by any
 * other production config row. A row that fails a check is skipped with its reason.
 * `dry_run` reports what would be deleted. Idempotent: a deleted row reports `not_found`.
 */
export const adminRepairBranchingOrphansBody = adminScopedTargetShape
  .extend({
    targets: z
      .array(
        z
          .object({
            entity_type: z.enum(BRANCHING_ORPHAN_ENTITY_TYPES),
            entity_id: z.string().min(1),
          })
          .strict(),
      )
      .min(1)
      .max(BRANCHING_ORPHAN_MAX_TARGETS)
      .optional(),
  })
  .strict()
  .superRefine((data, ctx) => {
    refineScopedTarget(data, ctx);
    // Deleting is per hub by construction: the operator names rows of ONE production hub.
    if (data.targets && data.scope !== 'hub') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'targets requires scope=hub', path: ['targets'] });
    }
  });

/**
 * POST /api/admin/organizations/repair-legacy-grants — enumerate and repair the
 * `member_grant` rows written by the platform-admin org-admin add before #3922
 * (the email landed in the `user_id` column and `user_email` stayed NULL).
 *
 * Org-level only, so this does NOT reuse `adminScopedTargetBody`: there is no
 * `hub` scope to give it, and accepting one would imply a hub roster that does
 * not exist. Idempotent; `dry_run` first.
 */
export const adminRepairLegacyOrgGrantsBody = z
  .object({
    scope: z.enum(['all', 'org']),
    org_id: z.string().uuid().optional(),
    dry_run: z.boolean().optional().default(false),
  })
  .strict()
  .superRefine((data, ctx) => {
    if (data.scope === 'org' && !data.org_id) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'scope=org requires org_id', path: ['org_id'] });
    }
    if (data.scope === 'all' && data.org_id) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'scope=all must not include org_id', path: ['org_id'] });
    }
  });

/**
 * POST /api/admin/organizations/repair-comped-plans — the heal owed beside the plan
 * PATCH's refusal. Dry-run by default, and `org_id` narrows the walk to
 * one organization so a repair can be rehearsed on a single row before the fleet.
 */
export const adminRepairCompedPlansBody = z
  .object({
    org_id: z.string().uuid().optional(),
    dry_run: z.boolean().optional().default(true),
  })
  .strict();

export const updatePricingPlanBody = z.object({
  display_name: z.string().min(1).optional(),
  stripe_product_id: z.string().optional(),
  lookup_key_prefix: z.string().optional(),
  messages_per_unit: z.number().int().min(1, 'messages_per_unit must be a positive integer').optional(),
  seats_per_unit: z.number().int().min(1, 'seats_per_unit must be a positive integer').optional(),
  seats_scaling: z.enum(['fixed', 'per_quantity']).optional(),
  is_active: z.boolean().optional(),
  // Rekor entitlement allowances. Editable here for the
  // same reason `messages_per_unit` is — and this route is the only ROUTINE way to change an
  // allowance on a live plan: the seed establishes a plan row and never rewrites one, so a
  // seed-version bump no longer moves these values (see `seed.ts`).
  //
  // It is not the only writer, and an operator should not read it as authoritative against
  // one. A dated owner pricing decision may also land as a bounded `PLATFORM_MIGRATIONS`
  // step naming its columns and rows, and such a step CAN supersede an edit made here —
  // deliberately, since an owner decision outranks an earlier operator edit of the same
  // field. `seed.ts`'s header calls this shape 2 and states its replay residual; v28 is the
  // first instance (`rekor_production_bases` on `free`, 2026-08-19).
  rekor_ops_tenths_included: z.number().int().min(0).optional(),
  rekor_max_records: z.number().int().min(0).optional(),
  rekor_import_rows_included: z.number().int().min(0).optional(),
  rekor_production_bases: z.number().int().min(0).max(1).optional(),
}).strip().refine(
  (data) => Object.keys(data).length > 0,
  { message: 'No fields to update' }
);

// =============================================================================
// INFERRED TYPES
// =============================================================================

export type UpdatePlatformConfigInput = z.infer<typeof updatePlatformConfigBody>;
export type UpdatePricingPlanInput = z.infer<typeof updatePricingPlanBody>;
export type AdminHarnessMassDestroyInput = z.infer<typeof adminHarnessMassDestroyBody>;
export type AdminRepairTeamOrphansInput = z.infer<typeof adminRepairTeamOrphansBody>;
export type AdminRepairBranchingOrphansInput = z.infer<typeof adminRepairBranchingOrphansBody>;
export type AdminRepairLegacyOrgGrantsInput = z.infer<typeof adminRepairLegacyOrgGrantsBody>;
export type AdminRepairKnowledgeBlobsInput = z.infer<typeof adminRepairKnowledgeBlobsBody>;

// =============================================================================
// RESPONSE TYPES
// =============================================================================

/** GET /admin/check */
export interface AdminCheckResponse {
  is_platform_admin: true;
}

/** Billing metric row returned inside organization list/detail */
export interface AdminBillingMetric {
  organization_id: string;
  metric_type: string;
  monthly_included: number;
  consumed_subscription: number;
}

/** Organization item in the paginated list */
export interface AdminOrganizationItem {
  organization_id: string;
  organization_name: string;
  created_at: string;
  plan_type: string;
  billing_status: string;
  has_subscription: boolean;
  period_end: string | null;
  owner_email: string | null;
  admin_users: Array<{
    organization_admin_user_id: string;
    user_email: string;
    is_owner: boolean;
  }>;
  metrics: AdminBillingMetric[];
}

/** Pagination metadata */
export interface AdminPaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/** GET /admin/organizations */
export interface AdminOrganizationsListResponse {
  data: AdminOrganizationItem[];
  pagination: AdminPaginationMeta;
}

/** POST /admin/organizations/:orgId/resync-registry */
export interface AdminResyncOrgRegistryResponse {
  ok: true;
  org_id: string;
  patched_fields: string[];
}

/** POST /admin/dev/backfill-org-registry */
export interface AdminBackfillOrgRegistryResponse {
  processed: number;
  succeeded: number;
  failed: number;
  failures: Array<{ org_id: string; error: string }>;
}

/** GET /admin/organizations/:orgId */
export interface AdminOrganizationDetailResponse {
  data: {
    organization_id: string;
    billing: Record<string, unknown> | null;
    metrics: AdminBillingMetric[];
    admin_users: Array<{
      organization_admin_user_id: string;
      user_email: string;
      is_owner: boolean;
    }>;
  };
}

/** POST /admin/organizations/:orgId/admins */
export interface AdminAddAdminUserResponse {
  data: {
    organization_admin_user_id: string;
    user_email: string;
    is_owner: boolean;
  };
}

/** GET /admin/config, PATCH /admin/config */
export interface AdminConfigResponse {
  data: Record<string, unknown>;
}

/** User search result item */
export interface AdminUserSearchItem {
  user_id: string;
  user_email: string;
  profile_name: string | null;
  max_free_orgs_override: number | null;
}

/** GET /admin/users/search */
export interface AdminUserSearchResponse {
  data: AdminUserSearchItem[];
}

/** PATCH /admin/users/:userId/free-org-limit */
export interface AdminUpdateFreeOrgLimitResponse {
  data: AdminUserSearchItem;
}

/** Per-conversation reap outcome inside the mass-destroy response. NEVER carries tokens/secrets. */
export interface AdminHarnessMassDestroyDetail {
  hub_id: string;
  conversation_id: string;
  /** True when the harness session's destroy succeeded (false ⇒ none to destroy, or the best-effort destroy failed). */
  sandbox_destroyed: boolean;
  /** True when the harness namespace was present and reaped (false on a dry_run or an idempotent no-op). */
  reaped: boolean;
}

/** One hub's dangling-team-reference repair (POST /admin/hubs/repair-team-orphans). */
export interface AdminRepairTeamOrphansDetail {
  hub_id: string;
  /** Team ids still referenced by a row after their `hub_team` was deleted. */
  dangling_team_ids: string[];
  /** Memberships purged (or, on a dry_run, that would be) — HubDO row + UserDO mirror. */
  team_users_removed: number;
}

/** POST /admin/hubs/repair-team-orphans — aggregate repair report. */
export interface AdminRepairTeamOrphansResponse {
  data: {
    scope: 'all' | 'org' | 'hub';
    dry_run: boolean;
    hubs_scanned: number;
    /** Hubs whose repair RPC threw and were skipped (partial-failure signal — re-run to cover them). */
    hubs_errored: number;
    /** Orgs whose hub listing threw, so their hubs never entered the scan. Same signal, one level up. */
    orgs_errored: number;
    /** Hubs found carrying at least one dangling team reference. */
    hubs_repaired: number;
    /** Memberships purged across all hubs (on a dry_run, the count that WOULD be purged). */
    team_users_removed: number;
    /** Per-hub breakdown; only hubs with something to repair are listed. */
    details: AdminRepairTeamOrphansDetail[];
  };
}

/** One hub's knowledge-blob repair (POST /admin/hubs/repair-knowledge-blobs). */
export interface AdminRepairKnowledgeBlobsHubDetail {
  hub_id: string;
  /** Keys listed under `{hub_id}/`, eval attachments and call openings left out. */
  keys_listed: number;
  /** Keys no `file` row holds, uploaded before the age window: the ones a run deletes. */
  keys_unreferenced: number;
  /** Unreferenced keys left for being younger than the window. */
  keys_too_recent: number;
  /** Keys deleted (0 on a dry run). */
  keys_deleted: number;
  /** The first `KNOWLEDGE_BLOB_MAX_KEYS_SHOWN` unreferenced keys, by name. */
  keys: string[];
  /**
   * The hub's sweep stopped on an error part-way: every count covers only the pages finished before
   * it (a page counts once its read and its delete are done), deletes included. It also counts in
   * `hubs_errored`; a re-run finishes it.
   */
  errored: boolean;
}

/** POST /admin/hubs/repair-knowledge-blobs — aggregate report. */
export interface AdminRepairKnowledgeBlobsResponse {
  data: {
    scope: 'all' | 'org' | 'hub';
    dry_run: boolean;
    hubs_scanned: number;
    /**
     * Hubs whose listing, read or delete threw, each stopped where it threw: its finished pages, deletes
     * included, are in the counts and its detail (`errored`). Re-run to finish them.
     */
    hubs_errored: number;
    /** Orgs whose hub listing threw, so their hubs never entered the scan. */
    orgs_errored: number;
    keys_listed: number;
    keys_unreferenced: number;
    keys_deleted: number;
    /** Per hub: each that held an unreferenced or too-recent key, or stopped part-way. */
    details: AdminRepairKnowledgeBlobsHubDetail[];
  };
}

/** Most referencing rows one result lists (see reason `referenced` for which of them can go first). */
export const BRANCHING_ORPHAN_MAX_REFERENCES_SHOWN = 20;

/**
 * Why a production hub could not be checked at all. Without a readable mapping from every
 * live preview, "no preview maps this row" cannot be established, so nothing is deleted.
 */
export type BranchingOrphanHubBlock =
  /** No live preview links to the hub, so there is no mapping to check a row against. */
  | 'no_live_preview'
  /** A live preview's mapping is absent or empty, so what it reaches is unknown. */
  | 'preview_mapping_missing';

/** Why a named row was left in place (POST /admin/hubs/repair-branching-orphans). */
export type BranchingOrphanSkipReason =
  | BranchingOrphanHubBlock
  /** No such row on the hub — already gone, which is what makes a re-run a no-op. */
  | 'not_found'
  /** A live preview's entity mapping reaches it: it is synced config, not an orphan. */
  | 'mapped'
  /**
   * Another production config row points at it (see `referenced_by`). A referrer of a type
   * this route deletes can be named first, in the same or an earlier run; any other referrer
   * (a resource's files, an experiment override, a template)
   * keeps the row in place.
   */
  | 'referenced'
  /** An org-linked (shadow) resource or one of its folders — production-only by design. */
  | 'production_only'
  /**
   * A channel whose routing key another channel on the same hub also uses. The delete
   * releases the HUB's routing claim, which would unroute the surviving channel.
   */
  | 'shares_routing_key'
  /** Checking or deleting the row threw (see `detail`); a re-run retries it. */
  | 'failed';

/** One named row's outcome (delete mode). */
export interface AdminBranchingOrphanTargetResult {
  entity_type: BranchingOrphanEntityType;
  entity_id: string;
  /** `would_delete` only on a dry_run. */
  outcome: 'deleted' | 'would_delete' | 'skipped';
  /** Set exactly when `outcome` is `skipped`. */
  reason: BranchingOrphanSkipReason | null;
  /** Live preview hub ids whose mapping reaches the row (reason `mapped`). */
  mapped_by: string[];
  /** `<entity_type>:<id>` of the rows pointing at it (reason `referenced`), capped. */
  referenced_by: string[];
  /** Context for `shares_routing_key` (the sibling channel) and `failed` (the error). */
  detail: string | null;
}

/**
 * An unmapped row that shares its identity with a mapped row of the same type (report
 * mode). A lead for the operator, not a verdict: review it, then name it with `--id`.
 */
export interface AdminBranchingTwinCandidate {
  entity_type: BranchingOrphanEntityType;
  /** The row no live preview maps. */
  entity_id: string;
  /** A row of the same type and identity that a live preview DOES map. */
  twin_id: string;
  /** The identity both rows share. */
  identity: string;
  /** `<entity_type>:<id>` of rows pointing at the unmapped row, capped (see reason `referenced`). */
  referenced_by: string[];
}

/** One production hub's result. */
export interface AdminRepairBranchingOrphansHubDetail {
  hub_id: string;
  /** Previews linked to the hub whose mappings were read. */
  live_preview_ids: string[];
  /** Set when the hub could not be checked; in delete mode every target is skipped with it. */
  blocked: BranchingOrphanHubBlock | null;
  /** Report mode only. */
  twin_candidates: AdminBranchingTwinCandidate[];
  /** Delete mode only: one entry per named row, in request order. */
  targets: AdminBranchingOrphanTargetResult[];
}

/** POST /admin/hubs/repair-branching-orphans — aggregate report. */
export interface AdminRepairBranchingOrphansResponse {
  data: {
    /** `delete` when the request named `targets`, else `report` (which never deletes). */
    mode: 'report' | 'delete';
    scope: 'all' | 'org' | 'hub';
    dry_run: boolean;
    /** Production hubs checked. Previews in scope are not repair targets and are not counted. */
    hubs_scanned: number;
    /** Hubs whose read threw and were skipped (partial-failure signal — re-run to cover them). */
    hubs_errored: number;
    /** Orgs whose hub listing threw, so their hubs never entered the scan. */
    orgs_errored: number;
    /** Hubs that could not be checked (see each detail's `blocked`). */
    hubs_blocked: number;
    /** Twin candidates across all hubs (report mode). */
    twin_candidates: number;
    /** Rows deleted (on a dry_run, the count that WOULD be deleted). */
    rows_deleted: number;
    /** Per-hub breakdown; report mode lists only hubs with a candidate or a block. */
    details: AdminRepairBranchingOrphansHubDetail[];
  };
}

/** One legacy roster row's disposition (POST /admin/organizations/repair-legacy-grants). */
export interface AdminRepairLegacyOrgGrantsDetail {
  org_id: string;
  /**
   * The email the row is keyed by — it sits in `member_grant.user_id`, not
   * `user_email`. Reported verbatim, NOT normalized: the operator matches this
   * against the roster, where a mixed-case key is stored as it was written.
   */
  user_email: string;
  /**
   * On a `dry_run` this is what WOULD happen; nothing is written.
   *   `repaired` — the email resolved to a real user: both halves of access written
   *                (OrgDO roster + UserDO membership) and the legacy row collapsed.
   *   `dropped`  — no user account for that email. The row conferred no access and
   *                only cost a seat, so it is deleted.
   *   `skipped`  — the row carries an owner grant, which this pass will not rewrite
   *                or delete (see the route's owner guard). Left for manual review.
   *   `errored`  — the repair threw; the row is unchanged. Re-run to retry it.
   */
  action: 'repaired' | 'dropped' | 'skipped' | 'errored';
  /** The resolved UserDO id on `repaired`; null for every other action. */
  user_id: string | null;
  /** Why, for `skipped` and `errored`. */
  reason?: string;
}

/** POST /admin/organizations/repair-legacy-grants — aggregate repair report. */
export interface AdminRepairLegacyOrgGrantsResponse {
  data: {
    scope: 'all' | 'org';
    dry_run: boolean;
    orgs_scanned: number;
    /** Orgs whose roster read threw and were skipped (partial-failure signal — re-run to cover them). */
    orgs_errored: number;
    /** Legacy rows found in scope; equals repaired + dropped + skipped + errored. */
    rows_found: number;
    rows_repaired: number;
    rows_dropped: number;
    rows_skipped: number;
    rows_errored: number;
    /** Per-row breakdown; only legacy rows are listed, so a healthy scope returns []. */
    details: AdminRepairLegacyOrgGrantsDetail[];
  };
}

/** One organization the comped-plan repair examined and changed. */
export interface AdminRepairCompedPlansDetail {
  org_id: string;
  /**
   * What happened to this row. `candidate` is every row under `dry_run` — matched, nothing
   * written. Explicit rather than inferred from the presence of `reason`, so the counts
   * below and the report agree without the reader reconstructing the rule.
   */
  outcome: 'candidate' | 'repaired' | 'errored';
  /**
   * Which unprojectable shape was found: no Stripe period at all, or one that has already
   * ended (a cancelled subscription's date, which `cancelBilling` leaves in place).
   */
  found: 'paid_without_period' | 'paid_with_ended_period';
  /** What the repair wrote (or would write, under `dry_run`). */
  plan_type: BillingPlanType;
  plan_key: PlanKey;
  /**
   * What the entitlement push driven by this repair's billing write did. Absent when
   * nothing was written (a `dry_run` candidate, or an errored row).
   *
   * - `not_owed` — the org is not Rekor-bearing, so no projection is owed yet. The expected
   *   result for the incident's own class: an org that never adopted a base, because every
   *   provisioning call was refused. The shape is fully repaired; nothing to chase.
   * - `pushed` — a fresh projection was recorded.
   * - `not_pushed` — a projection was owed and none landed. The sweep converges it, and it
   *   is the signal to look at the push logs.
   */
  push?: 'not_owed' | 'pushed' | 'not_pushed';
  /** Why, for `errored`. */
  reason?: string;
}

/** POST /admin/organizations/repair-comped-plans — aggregate repair report. */
export interface AdminRepairCompedPlansResponse {
  data: {
    dry_run: boolean;
    /** Candidate orgs examined — the registry-narrowed set, not the whole registry. */
    orgs_scanned: number;
    /** Orgs whose billing read threw and were skipped (re-run to cover them). */
    orgs_errored: number;
    /** Unprojectable rows confirmed in scope; equals `details.length`. */
    rows_found: number;
    /** Rows written. Always 0 under `dry_run`, where every row is a `candidate`. */
    rows_repaired: number;
    rows_errored: number;
    /** Per-org breakdown; only matched rows are listed, so a healthy fleet returns []. */
    details: AdminRepairCompedPlansDetail[];
  };
}

/** POST /admin/harness/mass-destroy — aggregate reap report. */
export interface AdminHarnessMassDestroyResponse {
  data: {
    scope: 'all' | 'org' | 'hub';
    dry_run: boolean;
    /** Distinct hubs scanned for live harness slots. */
    hubs_scanned: number;
    /** Hubs whose slot enumeration threw and were skipped (partial-failure signal — re-run to cover them). */
    hubs_errored: number;
    /** Orgs whose hub listing threw, so their hubs never entered the scan. Same signal, one level up. */
    orgs_errored: number;
    /** Live harness sessions discovered across all successfully-scanned hubs. */
    sessions_found: number;
    /** Sessions actually reaped (0 on a dry_run). */
    sessions_reaped: number;
    /** Sessions whose reap RPC threw and were skipped (0 on a dry_run; re-run to cover them). */
    sessions_errored: number;
    /** Harness sessions successfully destroyed (0 on a dry_run). */
    sandboxes_destroyed: number;
    details: AdminHarnessMassDestroyDetail[];
  };
}

/** GET /admin/pricing-plans */
export interface AdminPricingPlansListResponse {
  data: Array<Record<string, unknown>>;
}

/** PATCH /admin/pricing-plans/:planId */
export interface AdminUpdatePricingPlanResponse {
  data: Record<string, unknown>;
}

// One-time kanban slug migration result.
export interface KanbanSlugMigrationResult {
  dry_run: boolean;
  total_hubs: number;
  migrated: number;
  already_migrated: number;
  skipped: Array<{ hub_id: string; reason: string }>;
}

/** POST /admin/migrate/kanban-slugs */
export interface KanbanSlugMigrationResponse {
  data: KanbanSlugMigrationResult;
}

// =============================================================================
// DATA EXPLORER — Request Schemas
// =============================================================================

export const dataExplorerSearchQuery = z.object({
  q: z.string().min(1, 'search query is required'),
  type: z.enum(['org', 'hub', 'user']).optional(),
});

export const dataExplorerOrgIdParam = z.object({ orgId: uuidSchema });
export const dataExplorerHubIdParam = z.object({ hubId: uuidSchema });
export const dataExplorerConvIdParam = z.object({ convId: uuidSchema });

export const dataExplorerDoInstancesQuery = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  orphans_only: z.coerce.boolean().optional().default(false),
  /** Direct lookup by entity UUID (hub_id/org_id/user_id/conversation_id). When set, bypasses pagination and returns a synthesized entry via idFromName. */
  search: z.string().optional(),
});

export const dataExplorerNsIdParam = z.object({
  nsId: z.string().min(1, 'namespace ID is required'),
});

export const dataExplorerKvKeysBody = z.object({
  prefix: z.string().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export const dataExplorerKvKeyBody = z.object({
  key: z.string().min(1, 'key is required'),
});

/** Accepts UUID or Cloudflare DO hex ID (for orphan deletion) */
const uuidOrHexId = z.string().min(1);
export const dataExplorerDeleteOrgParam = z.object({ orgId: uuidOrHexId });
export const dataExplorerDeleteHubParam = z.object({ hubId: uuidOrHexId });
export const dataExplorerDeleteConvParam = z.object({ convId: uuidOrHexId });
export const dataExplorerDeleteUserParam = z.object({ userId: uuidOrHexId });

export const dataExplorerDeleteQuery = z.object({
  confirmation: z.string().min(1, 'confirmation is required'),
  cascade: z.coerce.boolean().optional().default(false),
});

export const dataExplorerKvDeleteBody = z.object({
  key: z.string().min(1, 'key is required'),
  confirmation: z.string().min(1, 'confirmation is required'),
});

// =============================================================================
// DATA EXPLORER — Response Types
// =============================================================================

/** Hub entry in org entity tree */
export interface DataExplorerHubEntry {
  hub_id: string;
  hub_name: string;
  hub_type: string;
  hub_environment: string;
  production_hub_id: string | null;
  dashboard_url: string | null;
}

/** Search result item */
export interface DataExplorerSearchResultItem {
  type: 'org' | 'hub' | 'user';
  id: string;
  name: string;
  /** Extra context (e.g., org name for hub results, email for user results) */
  context?: string;
}

/** GET /data-explorer/search */
export interface DataExplorerSearchResponse {
  data: DataExplorerSearchResultItem[];
}

/** GET /data-explorer/entity/org/:orgId */
export interface DataExplorerOrgEntityResponse {
  data: {
    organization_id: string;
    organization_name: string;
    dashboard_url: string | null;
    hubs: DataExplorerHubEntry[];
    /** Redacted via `SENSITIVE_COLUMNS.org.billing` before leaving the Worker. */
    billing: Record<string, unknown> | null;
    /** Column names masked in `billing`, so the UI can show the same footer the debug reads do. */
    redacted?: string[];
  };
}

/** GET /data-explorer/entity/hub/:hubId */
export interface DataExplorerHubEntityResponse {
  data: {
    hub_id: string;
    hub_name: string;
    hub_type: string;
    dashboard_url: string | null;
    conversation_count: number;
  };
}

/** GET /data-explorer/entity/conversation/:convId */
export interface DataExplorerConvEntityResponse {
  data: {
    conversation_id: string;
    dashboard_url: string | null;
    /** Redacted via `SENSITIVE_COLUMNS.conversation.conversation` before leaving the Worker. */
    conversation: Record<string, unknown> | null;
    /** Column names masked in `conversation`, so the UI can show the same footer the debug reads do. */
    redacted?: string[];
  };
}

/** DO namespace item */
export interface DataExplorerDoNamespace {
  id: string;
  name: string;
  class: string;
}

/** GET /data-explorer/do/namespaces */
export interface DataExplorerDoNamespacesResponse {
  data: DataExplorerDoNamespace[];
}

/** DO instance item */
export interface DataExplorerDoInstance {
  id: string;
  name: string | null;
  /** `null` means the backend did not probe CF for storage state (e.g., direct search by UUID) */
  hasStoredData: boolean | null;
  dashboard_url: string;
  /** The domain entity ID (e.g., org_id, hub_id) used in idFromName — null if unknown */
  entity_id?: string | null;
  /** Resolved domain name (e.g., org name) — null if not resolved */
  resolved_name?: string | null;
  /** Whether this instance is an orphan (not in any known hierarchy) */
  is_orphan?: boolean;
}

/** GET /data-explorer/do/namespaces/:nsId/instances */
export interface DataExplorerDoInstancesResponse {
  data: DataExplorerDoInstance[];
  cursor?: string;
}

/** KV namespace item */
export interface DataExplorerKvNamespace {
  name: string;
  id: string;
}

/** GET /data-explorer/kv/namespaces */
export interface DataExplorerKvNamespacesResponse {
  data: DataExplorerKvNamespace[];
}

/** KV key item */
export interface DataExplorerKvKeyItem {
  /** Display name. Masked when the key itself is credential material. */
  name: string;
  metadata: Record<string, unknown> | null;
  expiration?: number;
  /**
   * True when `name` was masked, so it is NOT a usable key identity. Clients must
   * not send it back as a view/delete target — the real key never leaves the
   * Worker for these entries.
   */
  masked?: boolean;
}

/** POST /data-explorer/kv/namespaces/:nsId/keys/list */
export interface DataExplorerKvKeysResponse {
  data: DataExplorerKvKeyItem[];
  cursor?: string;
}

/** POST /data-explorer/kv/namespaces/:nsId/keys/value */
export interface DataExplorerKvValueResponse {
  data: {
    key: string;
    value: string | null;
    masked: boolean;
  };
}

/** DELETE responses */
export interface DataExplorerDeleteResult {
  deleted: string[];
  failed: Array<{ id: string; error: string }>;
}

export interface DataExplorerDeleteResponse {
  data: DataExplorerDeleteResult;
}

// =============================================================================
// PITR (Point-in-Time Recovery) — Request Schemas
// =============================================================================

/** Valid DO namespace names for PITR operations */
export const pitrDoNamespace = z.enum(['HUB_DO', 'CONVERSATION_DO', 'ORG_DO', 'USER_DO', 'PLATFORM_DO']);

/** GET /admin/pitr/:namespace/:instanceId/bookmark */
export const pitrBookmarkParam = z.object({
  namespace: pitrDoNamespace,
  instanceId: z.string().min(1, 'instanceId is required'),
});

/** POST /admin/pitr/:namespace/:instanceId/restore — reuses bookmark param shape */
export const pitrRestoreParam = pitrBookmarkParam;

/** Body for restore request */
export const pitrRestoreBody = z.object({
  /** ISO 8601 timestamp to restore to (must be within last 30 days) */
  target_timestamp: z.string().datetime('target_timestamp must be a valid ISO 8601 datetime'),
  /** Must match the instanceId for confirmation */
  confirmation: z.string().min(1, 'confirmation is required'),
  /** If true, only fetch the bookmark without performing the restore (default: true) */
  dry_run: z.boolean().default(true),
});

// =============================================================================
// PITR — Inferred Types
// =============================================================================

export type PitrDoNamespace = z.infer<typeof pitrDoNamespace>;

// =============================================================================
// PITR — Response Types
// =============================================================================

/** GET /admin/pitr/:namespace/:instanceId/bookmark */
export interface PitrBookmarkResponse {
  data: {
    namespace: string;
    instance_id: string;
    current_bookmark: string;
  };
}

/** POST /admin/pitr/:namespace/:instanceId/restore */
export interface PitrRestoreResponse {
  data: {
    namespace: string;
    instance_id: string;
    target_timestamp: string;
    target_bookmark: string;
    current_bookmark: string;
    /** Bookmark that can be used to undo this restore */
    undo_bookmark: string | null;
    dry_run: boolean;
    restore_scheduled: boolean;
  };
}

// =============================================================================
// DO Health Validation — Response Types
// =============================================================================

export interface HealthValidationIssue {
  table: string;
  check: 'existence' | 'row_count' | 'json_validity';
  severity: 'error' | 'warning';
  message: string;
}

export interface HealthValidationResponse {
  data: {
    status: 'pass' | 'fail';
    tables_checked: number;
    tables_passed: number;
    issues: HealthValidationIssue[];
    checked_at: string;
  };
}

// =============================================================================
// Health Sampling — Request Schemas
// =============================================================================

/** POST /admin/health-sampling — trigger body */
export const healthSamplingTriggerBody = z.object({
  /** Number of instances to sample per DO type (default: 10) */
  sample_size: z.number().int().min(1).max(100).default(10),
  /** Filter to specific DO types (default: all) */
  do_types: z.array(pitrDoNamespace).optional(),
});

// =============================================================================
// DATA EXPLORER DEBUG READS — Request Schemas
// =============================================================================
//
// Platform-admin-only read-only inspection of DO SQLite state. Gated by
// platformAdminMiddleware + strict rate limit + kill switch + column redaction.
// See workers/backend/src/durable-objects/debug-redaction.ts for the central
// allowlist and redaction rules.

// The DO types (`dataExplorerDebugDoType`) and the `limit` bounds (`DEBUG_READ_MAX_LIMIT`,
// `DEBUG_READ_DEFAULT_LIMIT`) are declared in `./debug-read` (it says why).

export const DEBUG_DO_HEX_PREFIX = 'hex:';

/**
 * `hex:<64-hex>` is resolved via `idFromString` (raw CF DO id — for inspecting
 * DOs discovered without a reverse path, e.g. orphan-audit). All other forms
 * go through `idFromName`.
 */
const debugDoEntityId = z
  .string()
  .min(1, 'entity id is required')
  .max(68)
  .refine(
    (v) => !v.startsWith(DEBUG_DO_HEX_PREFIX) || /^hex:[0-9a-f]{64}$/.test(v),
    { message: 'hex: prefix must be followed by exactly 64 lowercase hex chars' },
  );

/**
 * Table name param. Two-layer injection defense: regex here + assertTableAllowed()
 * against a frozen allowlist in the DO RPC. Table names are NEVER string-interpolated
 * into SQL — they're looked up in a constant map.
 */
const debugTableName = z.string().regex(
  /^[a-z][a-z0-9_]{0,63}$/,
  'table name must be lowercase letters, digits, underscores (<=64 chars)',
);

/** GET /api/admin/data-explorer/debug/do/:type/:id/tables */
export const dataExplorerDebugTablesParam = z.object({
  type: dataExplorerDebugDoType,
  id: debugDoEntityId,
});

/** GET /api/admin/data-explorer/debug/do/:type/:id/tables/:name/rows */
export const dataExplorerDebugRowsParam = z.object({
  type: dataExplorerDebugDoType,
  id: debugDoEntityId,
  name: debugTableName,
});

export const dataExplorerDebugRowsQuery = z.object({
  limit: z.coerce.number().int().min(1).max(DEBUG_READ_MAX_LIMIT).default(DEBUG_READ_DEFAULT_LIMIT),
  /** Optional lookup by primary key value — typed as string since PKs are all TEXT UUIDs */
  id: z.string().min(1).max(256).optional(),
});

// =============================================================================
// DATA EXPLORER DEBUG READS — Inferred Types
// =============================================================================

// `DataExplorerDebugDoType` is declared in `./debug-read`.

// =============================================================================
// DATA EXPLORER DEBUG READS — Response Types
// =============================================================================

/** GET /api/admin/data-explorer/debug/do/:type/:id/tables */
export interface DataExplorerDebugTablesResponse {
  data: {
    do_type: DataExplorerDebugDoType;
    do_id: string;
    /** Tables present in the DO that are also in the debug allowlist */
    tables: string[];
  };
}

/** GET /api/admin/data-explorer/debug/do/:type/:id/tables/:name/rows */
export interface DataExplorerDebugRowsResponse {
  data: {
    do_type: DataExplorerDebugDoType;
    do_id: string;
    table: string;
    /** Rows with sensitive columns replaced by the string "[REDACTED]" */
    rows: Record<string, unknown>[];
    /** Column names that were redacted in this response (for operator awareness) */
    redacted_columns: string[];
    /** True if the result was capped at `limit` and more rows may exist */
    truncated: boolean;
    /** Applied limit — echoes the query param or its default */
    limit: number;
  };
}

// =============================================================================
// DATA EXPLORER DEBUG READS — ClickHouse analytics tables
// =============================================================================
//
// Same gates as the DO debug surface: platform-admin, audit, rate-limit, kill
// switch. CH tables are queried via parameterized SQL (`{key:Type}` placeholders)
// — never string-interpolated. Allowlist + redaction live in
// workers/backend/src/clickhouse/debug-redaction.ts.

/** Tables an admin can read via `wayai admin analytics`. */
export const dataExplorerDebugAnalyticsTable = z.enum([
  'conversation',
  'message',
  'schedule_event',
]);
export type DataExplorerDebugAnalyticsTable = z.infer<typeof dataExplorerDebugAnalyticsTable>;

/** UUID validator reused for filter values. Any non-UUID input is rejected before reaching CH. */
const debugUuid = z.string().uuid();

/** GET /api/admin/data-explorer/debug/analytics/:table/rows */
export const dataExplorerDebugAnalyticsRowsParam = z.object({
  table: dataExplorerDebugAnalyticsTable,
});

export const dataExplorerDebugAnalyticsRowsQuery = z.object({
  conversation_id: debugUuid.optional(),
  hub_id: debugUuid.optional(),
  message_id: debugUuid.optional(),
  limit: z.coerce.number().int().min(1).max(DEBUG_READ_MAX_LIMIT).default(DEBUG_READ_DEFAULT_LIMIT),
});

// =============================================================================
// DATA EXPLORER DEBUG READS — counts (wayai-pro/wayai#5142 step 5's census)
// =============================================================================
//
// Same gates as the DO debug reads: platform-admin, audit, rate limit, kill switch. The answer is
// counts and a hub's environment: a count reads the texts it classifies inside the DO, and no text
// leaves it.

/** The DOs the count capability reads: a hub, and an organization. */
export const dataExplorerDebugCountsDoType = dataExplorerDebugDoType.extract(['hub', 'org']);
export type DataExplorerDebugCountsDoType = z.infer<typeof dataExplorerDebugCountsDoType>;

/** GET /api/admin/data-explorer/debug/do/:type/:id/counts */
export const dataExplorerDebugCountsParam = z.object({
  type: dataExplorerDebugCountsDoType,
  id: debugUuid,
});

/** GET /api/admin/data-explorer/debug/do/:type/:id/counts */
export interface DataExplorerDebugCountsResponse {
  data:
    | { do_type: 'hub'; do_id: string; counts: DataExplorerDebugHubCounts; publish_state: DataExplorerDebugPublishState | null }
    | { do_type: 'org'; do_id: string; counts: DataExplorerDebugOrgCounts };
}

/** GET /api/admin/data-explorer/debug/analytics/tables */
export interface DataExplorerDebugAnalyticsTablesResponse {
  data: {
    tables: DataExplorerDebugAnalyticsTable[];
  };
}

/** GET /api/admin/data-explorer/debug/analytics/:table/rows */
export interface DataExplorerDebugAnalyticsRowsResponse {
  data: {
    table: DataExplorerDebugAnalyticsTable;
    rows: Record<string, unknown>[];
    /** Redacted column names or dotted JSON paths (e.g. "data", "data.meta.last_message_text"). */
    redacted_columns: string[];
    truncated: boolean;
    limit: number;
  };
}

// =============================================================================
// DATA EXPLORER DEBUG READS — R2 archive
// =============================================================================
//
// Admin-gated counterpart to `GET /api/archive/:hubId/conversations/:conversationId`.
// Same archive blob, but reachable only by platform admins and audited as
// `admin_debug_read`. Does not provision access — admins still need the blob to
// exist in R2.

// Hub+conversation pair shared by both debug-archive and debug-observability
// param schemas — keep them as one source of truth so route paths stay aligned.
const debugHubConvParam = z.object({
  hubId: debugUuid,
  conversationId: debugUuid,
});

// Optional message_id filter — same semantics for archive and observability
// (server returns the matching message; otherwise the full set).
const debugMessageIdQuery = z.object({
  message_id: debugUuid.optional(),
});

export const dataExplorerDebugArchiveParam = debugHubConvParam;
export const dataExplorerDebugArchiveQuery = debugMessageIdQuery;

/** GET /api/admin/data-explorer/debug/archive/:hubId/conversations/:conversationId */
export interface DataExplorerDebugArchiveResponse {
  data: {
    hub_id: string;
    conversation_id: string;
    /** The full archive blob written by the archive job, optionally with `messages` filtered server-side. */
    archive: Record<string, unknown>;
    /** True when ?message_id= filtered the messages array. */
    message_filtered: boolean;
  };
}

// =============================================================================
// DATA EXPLORER DEBUG READS — R2 LLM observability
// =============================================================================
//
// Admin-gated read of per-message OpenTelemetry GenAI records (full prompts,
// completions, tool calls, tokens, latency). The hub-scoped equivalent at
// `/api/conversations/:conversationId/...` enforces hub-user / team-member
// access; this debug variant requires `platform:admin` instead so an operator
// can inspect any hub for incident response. Same data shape both ways.

export const dataExplorerDebugObservabilityParam = debugHubConvParam;
export const dataExplorerDebugObservabilityQuery = debugMessageIdQuery;

/**
 * GET /api/admin/data-explorer/debug/observability/:hubId/conversations/:conversationId
 *
 * Discriminated by `kind` so TypeScript callers narrow without non-null assertions:
 * - `kind: 'list'` (no ?message_id) — `entries` is populated.
 * - `kind: 'single'` (with ?message_id) — `record` is populated.
 */
export type DataExplorerDebugObservabilityResponse =
  | {
      data: {
        kind: 'list';
        hub_id: string;
        conversation_id: string;
        entries: import('../observability/types').WayAIMetadataRecord[];
        /** True when the list was truncated by the server cap. */
        truncated: boolean;
      };
    }
  | {
      data: {
        kind: 'single';
        hub_id: string;
        conversation_id: string;
        record: import('../observability/types').LLMObservabilityRecord;
      };
    };

// =============================================================================
// Health Sampling — Response Types
// =============================================================================

export interface HealthSamplingInstanceResult {
  namespace: string;
  instance_id: string;
  status: 'pass' | 'fail' | 'error';
  issues: HealthValidationIssue[];
  error?: string;
}

export interface HealthSamplingReport {
  data: {
    status: 'pass' | 'fail';
    triggered_at: string;
    completed_at: string;
    commit?: string;
    sample_size: number;
    results: HealthSamplingInstanceResult[];
    summary: {
      total_instances: number;
      passed: number;
      failed: number;
      errors: number;
    };
  };
}
